import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { LoadingScreen } from '@truvhq/ui';
import { usePageVisibility } from 'react-page-visibility';

import { UnapprovedCompanyMember } from 'components/UnapprovedUser/UnapprovedCompanyMember';
import { UnapprovedUser } from 'components/UnapprovedUser/UnapprovedUser';
import { UnverifiedEmail } from 'components/UnverifiedEmail/UnverifiedEmail';
import { CompleteIndividual } from 'features/completeProfile/ui/CompleteIndividual';
import { CompleteProfile } from 'features/completeProfile/ui/CompleteProfile';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useSessionExpiredModal } from 'shared/ui/useSessionExpiredModal';
import { Error401UserView } from 'views/errors/Error401View';
import { ErrorNoAccess } from 'views/errors/ErrorNoAccess';
import { ErrorNotAuthenticated } from 'views/errors/ErrorNotAuthenticated';

type AuthGuardProps = {
  children?: ReactNode;
};

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const {
    isAuthenticated,
    wasAuthenticated,
    checkAuth,
    user,
    isGotError401,
    error,
    authFailed,
    authFailedError,
    permissions,
    login,
  } = useAuth();

  const isVisible = usePageVisibility();

  const { handleSessionExpired } = useSessionExpiredModal();

  useEffect(() => {
    if (isVisible && !isAuthenticated && wasAuthenticated) {
      handleSessionExpired();

      return;
    }

    checkAuth();
  }, [checkAuth, handleSessionExpired, isAuthenticated, isGotError401, isVisible, wasAuthenticated]);

  // this is initial login
  if (!wasAuthenticated && !authFailed) {
    void login();

    return <LoadingScreen className="min-h-full" />;
  }

  if (authFailed) {
    return <ErrorNotAuthenticated error={authFailedError} />;
  }

  if (isGotError401) {
    return <Error401UserView error={error} />;
  }

  if (!user?.isInformationFormComplete) {
    if (user?.isCompanyIndividual && user?.is_owner) {
      return <CompleteIndividual />;
    }

    if (!user?.isCompanyIndividual) {
      return <CompleteProfile showCompanyField={!!user?.is_owner} />;
    }
  }

  if (!user?.email_verified) {
    return <UnverifiedEmail />;
  }

  if (!user?.is_owner && !!user?.company && !user?.is_approved) {
    return <UnapprovedCompanyMember declined={user?.is_approved === false} />;
  }

  if (!user?.is_approved) {
    return <UnapprovedUser />;
  }

  if (!permissions.has('can_view_client_dashboard')) {
    return <ErrorNoAccess />;
  }

  return <>{children}</>;
};
